import pluralize from 'pluralize';
import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import styled from 'styled-components';

import Body2 from '@joinforma/ui/components/Body2';
import Caption from '@joinforma/ui/components/Caption';
import Overline from '@joinforma/ui/components/Overline';
import Title1 from '@joinforma/ui/components/Title1';
import Title2 from '@joinforma/ui/components/Title2';
import { ScreenWidth } from '@joinforma/ui/constants/breakpoints';

import PriceComparison from 'components/common/PriceComparison';
import type { Product } from 'components/common/types';
import { getImageURL } from 'utils/cloudinary';
import { VendorRoutes } from 'utils/routeGenerator';

interface SubscriptionPillProps {
  is_subscription: boolean;
}

const Card = styled(Link)`
  display: flex;
  flex-direction: column;
  width: 276px;
  height: 307px;
  background-color: ${({ theme }) => theme.colors.background.primary};
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.12),
    0px 1px 1px 1px rgba(0, 0, 0, 0.08);
  border-radius: 7px;
  position: relative;
  flex: 0 0 auto;

  @media (width: ${ScreenWidth.LAPTOP}) {
    width: 207px;
  }

  @media (width: ${ScreenWidth.TABLET_PORTRAIT}) {
    width: 100%;
  }
`;

const SubscriptionPill = styled.div<SubscriptionPillProps>`
  display: ${(props) => (props.is_subscription ? 'flex' : 'none')};
  flex-direction: row;
  align-items: center;
  padding: 8px 12px 6px;
  position: absolute;
  top: 10px;
  right: 10px;
  width: 113px;
  height: 26px;
  background: linear-gradient(0deg, #fccca0, #fccca0), #000000;
  border-radius: 16px;
`;

const PictureContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 155px;
  border-top-left-radius: 7px;
  border-top-right-radius: 7px;
`;

const Picture = styled.img`
  width: 100%;
  height: 155px;
  object-fit: cover;
  border-top-left-radius: 7px;
  border-top-right-radius: 7px;
`;

const SubContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: space-between;
  padding: 16px;
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ProductTitle = styled(Title1)`
  color : ${({ theme }) => theme.colors.text.primary};

  // prevent product title from overflowing into next line for alignment between cards
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
`;

const ProductSubtitle = styled(Body2)`
  color: ${({ theme }) => theme.colors.text.secondary};

  // this is a way to add ellipsis to multiline text that uses the least css
  // should have support in most modern browsers: https://caniuse.com/?search=webkit-line-clamp
  // reference: https://developer.mozilla.org/en-US/docs/Web/CSS/-webkit-line-clamp
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const VarientsTitle = styled(Caption)`
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const PriceVaries = styled(Title2)``;

const ProductCard = ({
  algolia_product,
  company,
  currency,
  exchange_rate = 1,
  onClick,
  origin,
}: {
  algolia_product: Product;
  company: {
    features: {
      stipend_config: {
        display_as_amount: boolean;
      };
    };
  };
  currency: string;
  exchange_rate?: number;
  onClick?: () => void;
  origin: string;
}) => {
  const { pathname, search } = useLocation();

  const {
    product_id,
    vendor_id,
    coupon_code_available,
    coupon_code_locations,
    product_instruction_image,
    product_image_url,
    vendor_title,
    vendor_subtitle,
    term,
    usd_special_price,
    usd_msrp,
    product_name,
    product_title,
    variations,
  } = algolia_product;

  const does_price_vary =
    coupon_code_available &&
    coupon_code_locations &&
    coupon_code_locations.length > 0;
  const is_subscription = term === 'monthly';

  const title = product_name
    ? product_name
    : product_title.includes(vendor_title)
    ? product_title
    : `${vendor_title} ${product_title}`;
  const image = product_instruction_image[0] ?? product_image_url;

  const variants_title = variations
    ? Object.values(variations?.options)
        .map((option) => pluralize(option.label, option.values.length, true))
        .join(' + ')
    : '';

  return (
    <Card
      onClick={onClick}
      to={VendorRoutes.detail({
        vendor_id,
        product_id,
      })}
      state={{ origin, prev: `${pathname}${search}` }}
    >
      <SubscriptionPill is_subscription={is_subscription}>
        <Overline>Subscription</Overline>
      </SubscriptionPill>
      <PictureContainer>
        <Picture src={getImageURL(image, { height: 190 })} alt={product_id} />
      </PictureContainer>
      <SubContainer>
        <TextContainer>
          <ProductTitle>{title}</ProductTitle>
          <ProductSubtitle>{vendor_subtitle}</ProductSubtitle>
          {variants_title && <VarientsTitle>{variants_title}</VarientsTitle>}
        </TextContainer>
        {does_price_vary ? (
          <PriceVaries>Price varies by location</PriceVaries>
        ) : (
          <PriceComparison
            company={company}
            currency={currency}
            exchange_rate={exchange_rate}
            new_price={usd_special_price || usd_msrp}
            new_layout={true}
            old_price={usd_msrp}
            saving_bubble={true}
            term={term}
          />
        )}
      </SubContainer>
    </Card>
  );
};

export default ProductCard;
